/**
 * JsonEditor — monospace textarea editor for JSON skill artifacts
 * (schema / template / enums / config). Live-validates, shows the parse
 * error inline, and only reports a parsed object upward when valid.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Textarea,
  Text,
  HStack,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';

export default function JsonEditor({ value, onChange, minH = '340px', isReadOnly = false }) {
  // `value` is the parsed object; text state is local.
  const initialText = useMemo(() => JSON.stringify(value ?? {}, null, 2), [value]);
  const [text, setText] = useState(initialText);
  const [error, setError] = useState(null);

  const bg = useColorModeValue('gray.50', 'gray.900');
  const border = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    setText(initialText);
    setError(null);
  }, [initialText]);

  const handleChange = (e) => {
    const next = e.target.value;
    setText(next);
    try {
      const parsed = JSON.parse(next);
      setError(null);
      if (onChange) onChange(parsed);
    } catch (err) {
      setError(err.message);
      if (onChange) onChange(undefined); // undefined = invalid, caller disables save
    }
  };

  const format = () => {
    try {
      const parsed = JSON.parse(text);
      setText(JSON.stringify(parsed, null, 2));
      setError(null);
      if (onChange) onChange(parsed);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box>
      <Textarea
        value={text}
        onChange={handleChange}
        isReadOnly={isReadOnly}
        fontFamily="mono"
        fontSize="xs"
        minH={minH}
        bg={bg}
        borderColor={error ? 'red.400' : border}
        spellCheck={false}
        resize="vertical"
      />
      <HStack mt={1} justify="space-between">
        <Text fontSize="xs" color={error ? 'red.400' : 'gray.500'}>
          {error ? `Invalid JSON: ${error}` : 'Valid JSON'}
        </Text>
        {!isReadOnly && (
          <Button size="xs" variant="ghost" onClick={format}>
            Format
          </Button>
        )}
      </HStack>
    </Box>
  );
}
